import "./BulkInvite.scss";
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Input, Select, Skeleton } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { env } from "../../../../env/env";
import { memberState } from "../../../../state/atoms";
import { PermissionPresetDto } from "../../../../generated/dashboard-client";
import PermissionPresetsApi from "../../../../api/PermissionPresetsApi";
import BulkInviteApi from "../../../../api/BulkInviteApi";
import AccountsApi from "../../../../api/AccountsApi";
import { handleServerError } from "../../../../util/ErrorHandler";

type Role = "admin" | "user";

interface ParsedRow {
  email: string;
  locations: string[];
  preset: string; // from the CSV preset column (may be empty)
}

type RowStatus = "Ready" | "Invalid email" | "Duplicate email" | "Account already exists" | "Location not found" | "Preset not found" | "No locations";

interface ReviewRow extends ParsedRow {
  index: number; // position in the source `rows` array, for inline edits
  effectivePreset: string;
  status: RowStatus;
}

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const TOTAL_STEPS = 3;

function presetParts(p: PermissionPresetDto, showItemLibrary: boolean): string[] {
  const parts: string[] = [];
  if (p.viewOnly) parts.push("View Only");
  if (p.dataOnly) parts.push("Edit Menu Data");
  if (p.manageDesigns) parts.push("Edit Designs");
  if (p.createTemplates) parts.push("Create Templates");
  if (p.publish) parts.push("Publish");
  if (p.orderPrint) parts.push("Order Print");
  return parts;
}

function presetPills(p: PermissionPresetDto, showItemLibrary: boolean): string[] {
  return presetParts(p, showItemLibrary);
}

function presetSummary(p: PermissionPresetDto, showItemLibrary: boolean): string {
  return presetParts(p, showItemLibrary).join(" · ");
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field); field = "";
    } else if (c === "\r") {
      // ignore
    } else if (c === "\n") {
      row.push(field); field = "";
      rows.push(row); row = [];
    } else field += c;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(cell => cell.trim().length > 0));
}

export function BulkInvite() {
  const navigate = useNavigate();
  const [member] = useRecoilState(memberState);
  const teamId = member.teamId ?? "";
  const showItemLibrary = member.showItemLibrary ?? false;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [role, setRole] = useState<Role>("user");

  const [presets, setPresets] = useState<PermissionPresetDto[]>([]);
  const [defaultPreset, setDefaultPreset] = useState("");
  const [locationNames, setLocationNames] = useState<Set<string>>(new Set());
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [locationCount, setLocationCount] = useState(0);

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);

  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; skipped: { email: string; reason: string }[] } | null>(null);

  useEffect(() => {
    if (!teamId || !member.id) return;
    setLoading(true);
    Promise.all([
      PermissionPresetsApi.getPresets(teamId),
      AccountsApi.getUserLocationsLight(member.id),
    ])
      .then(([presetsResp, locationsResp]) => {
        setPresets(presetsResp.data);
        const def = presetsResp.data.find(p => p.default) ?? presetsResp.data[0];
        setDefaultPreset(def?.name ?? "");
        const names = locationsResp.data.map(l => (l.name ?? "").trim()).filter(Boolean);
        setLocationNames(new Set(names.map(n => n.toLowerCase())));
        setLocationOptions([...names].sort((a, b) => a.localeCompare(b)));
        setLocationCount(names.length);
      })
      .catch(handleServerError)
      .finally(() => setLoading(false));
  }, [teamId, member.id]);

  const reviewRows: ReviewRow[] = useMemo(() => {
    const seen = new Set<string>();
    return rows.map((row, index) => {
      const base = { ...row, index };
      const email = row.email.trim();
      if (!EMAIL_REGEX.test(email)) return { ...base, effectivePreset: "", status: "Invalid email" as RowStatus };

      const key = email.toLowerCase();
      if (seen.has(key)) return { ...base, effectivePreset: "", status: "Duplicate email" as RowStatus };
      seen.add(key);

      if (existingEmails.has(key)) return { ...base, effectivePreset: "", status: "Account already exists" as RowStatus };

      if (role === "admin") return { ...base, effectivePreset: "", status: "Ready" as RowStatus };

      const effectivePreset = (row.preset || defaultPreset).trim();
      if (!effectivePreset || !presets.some(p => p.name?.toLowerCase() === effectivePreset.toLowerCase())) {
        return { ...base, effectivePreset, status: "Preset not found" as RowStatus };
      }
      if (row.locations.length === 0) return { ...base, effectivePreset, status: "No locations" as RowStatus };
      const missing = row.locations.find(l => !locationNames.has(l.trim().toLowerCase()));
      if (missing) return { ...base, effectivePreset, status: "Location not found" as RowStatus };

      return { ...base, effectivePreset, status: "Ready" as RowStatus };
    });
  }, [rows, role, defaultPreset, presets, locationNames, existingEmails]);

  function updateRow(index: number, patch: Partial<ParsedRow>) {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  const readyRows = useMemo(() => reviewRows.filter(r => r.status === "Ready"), [reviewRows]);
  const errorCount = reviewRows.length - readyRows.length;
  const distinctLocations = useMemo(() => {
    if (role === "admin") return locationCount;
    return new Set(rows.flatMap(r => r.locations.map(l => l.trim().toLowerCase())).filter(Boolean)).size;
  }, [rows, role, locationCount]);

  function handleFile(file: File) {
    setFileName(file.name);
    setExistingEmails(new Set());
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ""));
      if (parsed.length === 0) { setRows([]); return; }

      const firstRow = parsed[0].map(h => h.trim().toLowerCase());
      const hasHeader = firstRow.includes("email");
      const emailIdx = hasHeader ? firstRow.indexOf("email") : 0;
      const locationsIdx = hasHeader ? firstRow.indexOf("locations") : 1;
      const presetIdx = hasHeader ? firstRow.indexOf("preset") : 2;
      const dataRowsRaw = hasHeader ? parsed.slice(1) : parsed;

      const dataRows: ParsedRow[] = dataRowsRaw.map(cols => ({
        email: (emailIdx >= 0 ? cols[emailIdx] : cols[0] ?? "").trim(),
        locations: locationsIdx >= 0 && cols[locationsIdx]
          ? cols[locationsIdx].split(",").map(s => s.trim()).filter(Boolean)
          : [],
        preset: presetIdx >= 0 ? (cols[presetIdx] ?? "").trim() : "",
      })).filter(r => r.email.length > 0);

      setRows(dataRows);

      const emails = dataRows.map(r => r.email).filter(Boolean);
      if (teamId && emails.length > 0) {
        BulkInviteApi.validateEmails(teamId, emails)
          .then(resp => setExistingEmails(new Set((resp.data ?? []).map(i => (i.email ?? "").toLowerCase()))))
          .catch(() => setExistingEmails(new Set()));
      }
    };
    reader.readAsText(file);
  }

  function downloadTemplate() {
    const content = role === "admin"
      ? "email\njsmith@sagora.com\nkwong@sagora.com\n"
      : "email,locations,preset\n" +
        'user1@companyname.com,"Austin Downtown",View Only\n' +
        'user2@companyname.com,"Austin Downtown, Austin North",Data Only Staff\n';
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-invite-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  const send = useCallback(() => {
    if (!teamId || readyRows.length === 0) return;
    setSending(true);
    BulkInviteApi.bulkInvite(teamId, {
      ownerId: member.id,
      role,
      rows: readyRows.map(r => ({
        email: r.email,
        locations: role === "admin" ? [] : r.locations,
        preset: role === "admin" ? undefined : r.effectivePreset,
      })),
    })
      .then(resp => {
        setResult({
          sent: resp.data.sent ?? 0,
          skipped: (resp.data.skipped ?? []).map(s => ({ email: s.email ?? "", reason: s.reason ?? "" })),
        });
        setStep(4);
      })
      .catch(handleServerError)
      .finally(() => setSending(false));
  }, [teamId, readyRows, member.id, role]);

  function reset() {
    setStep(1);
    setRole("user");
    setFileName("");
    setRows([]);
    setExistingEmails(new Set());
    setErrorsOnly(false);
    setResult(null);
  }

  const displayRows = errorsOnly ? reviewRows.filter(r => r.status !== "Ready") : reviewRows;

  return (
    <div className={"content bulk-invite"}>
      <a className={"back"} onClick={() => navigate(env.dashboardPath + "/account/team")}>
        <ArrowLeftOutlined /> Back to team
      </a>

      {step < 4 &&
        <div className={"heading"}>
          <h3>Bulk invite</h3>
          <p>Upload a CSV to invite multiple users at once.</p>
        </div>}

      {step < 4 &&
        <div className={"steps"}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => {
            const n = i + 1;
            return (
              <div className={`step${n === step ? " active" : ""}`} key={n}>
                <span className={"circle"}>{n}</span>
                {n < TOTAL_STEPS && <span className={"connector"} />}
              </div>
            );
          })}
        </div>}

      {loading && <Skeleton active />}

      {!loading && step === 1 &&
        <div className={"step-role"}>
          <h4 className={"step-title"}>Select a role</h4>
          <p className={"step-sub"}>All users in this batch will share the same role. Run separate imports for different roles.</p>

          <div className={"role-cards"}>
            <div className={`role-card${role === "admin" ? " selected" : ""}`} onClick={() => setRole("admin")}>
              <div className={"rc-head"}><SafetyCertificateOutlined /> Admin</div>
              <p className={"rc-desc"}>Full access to all features, user management, and all locations.</p>
              <p className={"rc-note"}>Admins don't need location or permission columns in the CSV.</p>
            </div>
            <div className={`role-card${role === "user" ? " selected" : ""}`} onClick={() => setRole("user")}>
              <div className={"rc-head"}><UserOutlined /> User</div>
              <p className={"rc-desc"}>Access is scoped to assigned locations with specific permissions.</p>
              <p className={"rc-note"}>Specify locations and a preset in your CSV per user.</p>
            </div>
          </div>

          <div className={"actions"}>
            <Button type={"primary"} size={"large"} onClick={() => setStep(2)}>Next</Button>
          </div>
        </div>}

      {!loading && step === 2 &&
        <div className={"step-upload"}>
          <h4 className={"step-title"}>Upload CSV</h4>
          <p className={"step-sub"}>All users in this batch will share the same role. Run separate imports for different roles.</p>

          <div
            className={`dropzone${dragOver ? " drag" : ""}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <CloudUploadOutlined className={"upload-icon"} />
            <div className={"dz-main"}>Drag and drop your CSV file here or <span className={"link"}>select file from your computer</span></div>
            <div className={"dz-hint"}>
              <ExclamationCircleOutlined /> Required columns: {role === "admin" ? "email" : "email, locations, preset"}
            </div>
            {fileName && <div className={"dz-file"}>{fileName} — {rows.length} rows</div>}
            <input ref={fileInputRef} type={"file"} accept={".csv"} hidden onChange={onFileInput} />
          </div>

          <a className={"template-link"} onClick={downloadTemplate}>
            <DownloadOutlined /> Download CSV template
          </a>

          {role === "user" &&
            <div className={"default-preset"}>
              <div className={"section-label"}>SELECT DEFAULT PRESET</div>
              <p className={"section-sub"}>Applied to any row that doesn't include a preset column.</p>
              <div className={"preset-cards"}>
                {presets.map(p => (
                  <div
                    className={`preset-card${defaultPreset === p.name ? " selected" : ""}`}
                    key={p.id}
                    onClick={() => setDefaultPreset(p.name ?? "")}
                  >
                    <div className={"pc-name"}>{p.name}</div>
                    <div className={"pc-summary"}>{presetSummary(p, showItemLibrary)}</div>
                    <div className={"pc-pills"}>
                      {presetPills(p, showItemLibrary).map(pill => <span className={"pill"} key={pill}>{pill}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>}

          <div className={"actions"}>
            <Button size={"large"} onClick={() => setStep(1)}>Back</Button>
            <Button type={"primary"} size={"large"} disabled={rows.length === 0} onClick={() => setStep(3)}>Next</Button>
          </div>
        </div>}

      {!loading && step === 3 &&
        <div className={"step-review"}>
          <h4 className={"step-title"}>Review</h4>
          <p className={"step-sub"}>Review your invited users or re-upload your CSV.</p>

          <div className={"stats"}>
            <div className={"stat"}><span className={"num"}>{reviewRows.length}</span><span className={"cap"}>Users</span></div>
            <div className={"stat"}><span className={"num"}>{distinctLocations}</span><span className={"cap"}>Locations</span></div>
            <div className={"stat"}><span className={"num"}>{role === "admin" ? "Admin" : "User"}</span><span className={"cap"}>Role</span></div>
          </div>

          {errorCount > 0 &&
            <div className={"error-banner"}>
              <ExclamationCircleOutlined /> {errorCount} {errorCount === 1 ? "row has" : "rows have"} issues — fix them inline below, or re-upload your CSV.
            </div>}

          <div className={"review-table"}>
            <table>
              <thead>
                <tr><th>Email</th><th>Role</th><th>Locations</th><th>Preset</th><th>Status</th></tr>
              </thead>
              <tbody>
                {displayRows.map((r) => {
                  const ok = r.status === "Ready";
                  const emailErr = r.status === "Invalid email" || r.status === "Duplicate email" || r.status === "Account already exists";
                  const locErr = r.status === "Location not found" || r.status === "No locations";
                  const presetErr = r.status === "Preset not found";
                  return (
                    <tr className={ok ? "" : "error"} key={`row-${r.index}`}>
                      <td>
                        {emailErr
                          ? <Input
                              className={"cell-input"}
                              size={"small"}
                              value={r.email}
                              placeholder={"name@company.com"}
                              onChange={e => updateRow(r.index, { email: e.target.value })}
                            />
                          : r.email}
                      </td>
                      <td>{role === "admin" ? "Admin" : "User"}</td>
                      <td>
                        {role === "admin"
                          ? "All locations"
                          : locErr
                            ? <Select
                                className={"cell-select"}
                                size={"small"}
                                mode={"multiple"}
                                allowClear
                                placeholder={"Select locations"}
                                value={r.locations}
                                options={locationOptions.map(name => ({ label: name, value: name }))}
                                onChange={(vals: string[]) => updateRow(r.index, { locations: vals })}
                              />
                            : r.locations.join(", ")}
                      </td>
                      <td>
                        {role === "admin"
                          ? "—"
                          : presetErr
                            ? <Select
                                className={"cell-select"}
                                size={"small"}
                                placeholder={"Select preset"}
                                value={r.preset || undefined}
                                options={presets.map(p => ({ label: p.name ?? "", value: p.name ?? "" }))}
                                onChange={(val: string) => updateRow(r.index, { preset: val })}
                              />
                            : <span className={"preset-pill"}>{r.effectivePreset || "—"}</span>}
                      </td>
                      <td className={ok ? "ready" : "status-err"}>{r.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={"table-footer"}>
            Showing {displayRows.length} of {reviewRows.length} rows
            {errorCount > 0 && <> - <span className={"link"} onClick={() => setErrorsOnly(v => !v)}>{errorsOnly ? "Show all" : "Show errors only"}</span></>}
          </div>

          <div className={"actions"}>
            <Button size={"large"} onClick={() => setStep(2)}>Re-upload CSV</Button>
            <Button type={"primary"} size={"large"} loading={sending} disabled={readyRows.length === 0} onClick={send}>
              Send {readyRows.length} invites
            </Button>
          </div>
        </div>}

      {step === 4 && result &&
        <div className={"step-done"}>
          <SendOutlined className={"done-icon"} />
          <div className={"done-title"}>{result.sent} invites sent</div>
          <p className={"done-sub"}>
            Users will receive an email to set up their account.
            {result.skipped.length > 0 && ` ${result.skipped.length} ${result.skipped.length === 1 ? "row was" : "rows were"} skipped.`}
          </p>

          {result.skipped.length > 0 &&
            <div className={"skipped-list"}>
              {result.skipped.map((s, i) => (
                <div className={"skipped-row"} key={`${s.email}-${i}`}>
                  <span className={"s-email"}>{s.email}</span>
                  <span className={"s-reason"}>{s.reason}</span>
                </div>
              ))}
            </div>}

          <Button type={"primary"} size={"large"} onClick={reset}>Invite more users</Button>
        </div>}
    </div>
  );
}
